import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useSession } from '../lib/sessionContext'

/**
 * Re-reads `/api/session` while the app is open, so a gate that appears mid-session is acted
 * on rather than waiting for the next full page load.
 *
 * WHY IT EXISTS. The bootstrap read the session exactly once. `SessionGate` re-applies its
 * ROUTING decision on every navigation, but against the same frozen `Gate` from boot — so an
 * account suspended, expired or newly asked to accept terms kept using the panel for as long
 * as the tab stayed open. In the legacy product every navigation was a server request and
 * `ForceRedirectionMiddleware` caught that on the user's next click; in an SPA navigation
 * never reaches the server, and the middleware deliberately skips `/api`.
 *
 * WHY IT LIVES HERE AND NOT IN `SessionGate`. `SessionGate` is instantiated three times in
 * `App.jsx` — the admin shell, `/portal/*`, and `/general/report`. Moving between those
 * branches UNMOUNTS one instance and MOUNTS another, which would reset the refs below and
 * skip the check on exactly the cross-branch navigations most worth making it. This is
 * mounted once, inside the provider, and renders nothing — the same shape as `DocumentTitle`.
 *
 * WHAT TRIGGERS A READ:
 *
 *   1. A PATHNAME CHANGE, unthrottled. A navigation is the moment the user asks for
 *      something new, which is when a stale verdict starts doing harm. It is deliberately
 *      not time-throttled: any window long enough to save real load is long enough to
 *      suppress the first navigation after boot. Keyed on `pathname` ALONE, so table paging,
 *      filters and the `#tab` hashes — all of which change the location — do not trigger it.
 *
 *   2. THE TAB BECOMING VISIBLE, throttled. `visibilitychange` and `focus` fire on every
 *      alt-tab with no user intent behind them, and `/api/session` is not a cheap endpoint,
 *      so this one is rate-limited.
 *
 * There is deliberately NO POLL. A timer would run against every open tab forever and buys
 * only the case the two triggers miss — someone sitting on one screen, focused, not
 * navigating, who is by definition not doing anything a gate needs to stop. Better to name
 * that gap than to hide a timer in the app.
 *
 * A failed re-read leaves the session it already has: `revalidate` is `load(true)`, whose
 * catch returns rather than setting the error state, because `SessionGate` renders its error
 * state INSTEAD of the page and would unmount whatever the user had open.
 */
const FOCUS_THROTTLE_MS = 30_000

export default function SessionRevalidator() {
  const { revalidate } = useSession()
  const { pathname } = useLocation()

  /* Seeded with the path the app booted on, so the bootstrap's own read is not immediately
     repeated by this effect's first run. */
  const lastPath = useRef(pathname)
  const inFlight = useRef(false)
  const lastFocusRead = useRef(0)

  const read = useCallback(async () => {
    // Click-storming the rail must not pile up reads against a ~25-query endpoint.
    if (inFlight.current) return
    inFlight.current = true
    try {
      await revalidate()
    } finally {
      inFlight.current = false
    }
  }, [revalidate])

  useEffect(() => {
    if (pathname === lastPath.current) return
    lastPath.current = pathname
    read()
  }, [pathname, read])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return

      const now = Date.now()
      if (now - lastFocusRead.current < FOCUS_THROTTLE_MS) return
      lastFocusRead.current = now
      read()
    }

    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      // BOTH, or StrictMode's double-invoke accumulates handlers in dev.
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [read])

  return null
}
